import Stack from "@mui/material/Stack"
import Suspenseful from "components/Suspenseful"
import SectionTitle from "components/UI/SectionTitle"
import { PageSection } from "components/UI/Structure/PageSection"
import UploadedDocuments from "./UploadedDocuments"
import { useParams } from "react-router-dom"
import { useCurrentUser } from "lib/CurrentUserProvider"
import { FormTextField, getIn, useFormikContext } from "components/Formik/forms"
import { useBookends } from "lib/BookendsProvider"
import { useCallback, useEffect, useState } from "react"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"
import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import COLORS from 'lib/colors';
import { useMutation, useSuspenseQuery } from "@apollo/client"
import { GET_DOCUMENTS_QUERY, UPDATE_CLIENT } from "./queries"
import airbrake from "shared/airbrake"
import StandardDialog from "components/UI/Dialog/StandardDialog"
import Typography from "@mui/material/Typography"
import FormWrapper from "components/Formik/FormWrapper"
import { uploadDocumentSchema } from "./yupSchemas"
import { removeFileExtension, sanitizeFilename } from "lib/utils"
import FilePickerWithLabel from "components/UI/FilePickerWithLabel/FilePickerWithLabel"
import { useNotifications } from "lib/NotificationsProvider"
import Button from "@mui/material/Button"

const ALLOWED_EXTENSIONS = ['doc','docx','pdf','csv','xls','xlsx','txt']

function UploadOrEditDocumentDialog({onClose, onComplete, isEditing, existingFile}) {
  const [file, setFile] = useState<File>(existingFile)
  const [openDelete, setOpenDelete] = useState(false)

  const { values, setValues, submitForm, isSubmitting, setSubmitting } = useFormikContext<Document>()
  const { addNotification } = useNotifications()
  const { clientId } = useParams()

  const handleError = error => {
    addNotification({ variant: 'alert', message: "Failed to attach document" })
    airbrake.notify({ error: error, message: "Failed to attach document" })
  }

  const setFileAndName = (file) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;

      const [prefix, base64Data] = result.split(',')
      const mimeMatch = prefix.match(/^data:([^;]+);/)
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
      const modifiedPrefix = `data:${mime};name=${sanitizeFilename(file.name)};base64`
      const modifiedDataUrl = `${modifiedPrefix},${base64Data}`

      setFile(file)
      setValues(values => ({
        ...values,
        name: removeFileExtension(file.name),
        fileUrl: modifiedDataUrl,
      }))
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
    };

    reader.readAsDataURL(file);
  }

  const [gqlUpdate] = useMutation(UPDATE_CLIENT)
  const deleteDocument = useCallback(() => {
    setSubmitting(true)

    const variables = {
      id: clientId ?? '',
      destroyDocuments: [values.id ?? '']
    }

    gqlUpdate({ variables: variables }).then(() => {
      addNotification({ variant: 'notice', message: `Deleted "${values.name}"` })
      onComplete()
    }).catch(_error => {
      addNotification({ variant: 'alert', message: `Trouble deleting document. Please try again later.` })
    }).finally(() => {
      setSubmitting(false)
    })
  }, [setSubmitting, clientId, values.id, values.name, gqlUpdate, addNotification, onComplete])

  return (
    <>
      {!openDelete &&
        <StandardDialog
          title={`${isEditing ? 'Edit' : 'Upload' } Document`}
          onCancel={onClose}
          onClose={onClose}
          onConfirm={() => submitForm()}
          disabled={!file}
          loading={isSubmitting}
          confirmText={isEditing ? 'Save' : 'Upload'}
          maxWidth='600px'
          footer={isEditing && <Button variant="outlined" onClick={() => setOpenDelete(true)}>Delete</Button>}
          description={
            <>
              <FilePickerWithLabel
                file={file}
                setFile={setFile}
                fileDescriptor='Document'
                extensions={ALLOWED_EXTENSIONS}
                onChange={setFileAndName}
                onError={handleError}
                isSubmitting={isSubmitting}
              />
              <Typography sx={{color: COLORS.cadetGray}}>Valid extensions: {ALLOWED_EXTENSIONS.join(', ')}</Typography>
            </>
          }
        >
          <Stack direction={'column'} gap={2}>
            <FormTextField
              label='Document Name'
              name='name'
              required
              sx={{ pr: 7 }}
            />
            <FormTextField
              label='Description'
              name='description'
              sx={{ pr: 7 }}
            />
          </Stack>
        </StandardDialog>
      ||
        <StandardDialog
          title='Delete Document'
          onCancel={() => setOpenDelete(false)}
          onClose={() => setOpenDelete(false)}
          onConfirm={() => deleteDocument()}
          confirmText='Delete'
          maxWidth='600px'
          description={<>Do you want to delete <strong>{values.name}</strong>?</>}
        />
      }
    </>
  )
}

type Document = {
  id?: string
  description: string
  fileUrl: string
  name: string
  filename: string
}

type UploadOrEditDocumentContainerProps = {
  onClose: () => void
  onComplete: () => void
  isEditing?: boolean
  document?: Partial<Document>
}

export function UploadOrEditDocumentContainer({ onClose, onComplete, isEditing = false, document = {} }: UploadOrEditDocumentContainerProps) {
  const { addNotification } = useNotifications()
  const { clientId } = useParams()

  const [gqlUpdate] = useMutation(UPDATE_CLIENT)

  const createOrUpdateDocument = useCallback((values, { setSubmitting }) => {
    setSubmitting(true)

    const documentInput = {
      ...(isEditing && { id: values.id }),
      description: values.description ?? '',
      ...(values.fileUrl && { fileUrl: values.fileUrl }),
      name: values.name,
    }
    const variables = {
      id: clientId ?? '',
      ...(isEditing && { updateDocuments: [documentInput] }),
      ...(!isEditing && { createDocuments: [documentInput] }),
    }

    gqlUpdate({ variables: variables }).then(() => {
      addNotification({ variant: 'notice', message: `${isEditing ? 'Sav' : 'Upload'}ed "${values.name}"` })
      onComplete()
    }).catch(_error => {
      addNotification({ variant: 'alert', message: `Trouble ${isEditing ? 'sav' : 'upload'}ing document. Please try again later.` })
    }).finally(() => {
      setSubmitting(false)
    })
  }, [isEditing, clientId, gqlUpdate, addNotification, onComplete])

  return(
    <FormWrapper data={document} schema={uploadDocumentSchema} onSubmit={createOrUpdateDocument}>
      {/* When editing, document is passed in - pass it's filename on as the file name for the FilePicker to display */}
      <UploadOrEditDocumentDialog onClose={onClose} onComplete={onComplete} isEditing={isEditing} existingFile={document.filename && {name: document.filename}} />
    </FormWrapper>
  )
}

export default function ClientDocumentPage() {
  const { clientId } = useParams()
  const { getClientRole } = useCurrentUser()
  const hasPermitEditRole = !!getIn(getClientRole(clientId), 'attributes.permitEdit')
  const [uploadOpen, setUploadOpen] = useState(false)

  const { setBookends } = useBookends()
  useEffect(() => {
    hasPermitEditRole && setBookends({
      footer: {
        buttons: [
          {
            type: 'primary',
            label: 'Upload Document',
            disabled: false,
            onClick: () => setUploadOpen(true),
          }
        ]
      }
    })
    // clientId needed (despite not being used) to ensure setBookends happens when client changes
  }, [hasPermitEditRole, setBookends, clientId])

  const { data: { documents }, refetch } = useSuspenseQuery(GET_DOCUMENTS_QUERY, {
    variables: { clientId: clientId }
  })

  return (
    <>
      <PageSection>
        <Stack direction='row' gap={2} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: '-2px' }}>
          <SectionTitle title='Documents' />
          {hasPermitEditRole &&
            <Tooltip title='Upload Document'>
              <IconButton onClick={() => setUploadOpen(true)} sx={{ color: COLORS.frenchBlue, padding: '3px' }}>
                <UploadIcon />
              </IconButton>
            </Tooltip>
          }
        </Stack>
        <Suspenseful
          component={UploadedDocuments}
          userCanEdit={hasPermitEditRole}
          data={documents}
          refetch={refetch}
        />
      </PageSection>

      {uploadOpen && (
        <UploadOrEditDocumentContainer onClose={() => { setUploadOpen(false) }} onComplete={() => { refetch(); setUploadOpen(false) }} />
      )}
    </>
  )
}
